import { MixPanelEvents } from '../../mixPanelEvents';
import { OutreachTypes } from '../../urls';

export type TrackingUser = {
  id?: string;
  emailAddress?: string;
  role?: string;
};

export enum AvailableScreens {
  HealthSummary = 'HealthSummary',
  Monitors = 'Monitors',
  MonitorConfig = 'MonitorConfig',
  OutreachConfig = 'OutreachConfig',
  MonitorDetail = 'MonitorDetail',
  MonitorAdd = 'MonitorAdd',
  Configuration = 'Configuration',
  MonitorEdit = 'MonitorEdit',
  AdminPanel = 'AdminPanel',
  Settings = 'Settings',
  Login = 'Login',
}

type GenericTrackEventParams = {
  eventName: MixPanelEvents.LOGOUT;
  additionalContent?: Record<string, any>;
};

type OutreachBaseContent = {
  PARTNER_ID: string;
  MONITOR_ID: string;
  ENABLED: boolean;
};

export type TrackEventParams =
  | GenericTrackEventParams
  | {
      eventName:
        | MixPanelEvents.AUTHENTICATION_SUCCESS
        | MixPanelEvents.AUTHENTICATION_FAILURE;
      additionalContent?: {
        ERROR_MESSAGE?: string;
      };
    }
  | {
      eventName: MixPanelEvents.SCREEN;
      additionalContent: {
        SCREEN: AvailableScreens;
      };
    }
  | {
      eventName: MixPanelEvents.MONITOR_ADD;
      additionalContent?: undefined;
    }
  | {
      eventName: MixPanelEvents.PARTNER_CHANGE;
      additionalContent?: undefined;
    }
  | {
      eventName: MixPanelEvents.MONITOR_TIME_RANGE_SELECT;
      additionalContent: {
        TIME_RANGE: string;
      };
    }
  | {
      eventName: MixPanelEvents.MONITOR_ADD;
      additionalContent?: undefined;
    }
  | {
      eventName:
        | MixPanelEvents.MONITOR_DOWNLOAD
        | MixPanelEvents.MONITOR_EDIT
        | MixPanelEvents.MONITOR_DELETE;
      additionalContent: {
        MONITOR_ID?: string;
      };
    }
  | {
      eventName:
        | MixPanelEvents.MONITOR_ADD_SUCCESS
        | MixPanelEvents.MONITOR_EDIT_SUCCESS
        | MixPanelEvents.MONITOR_ADD_FAILURE
        | MixPanelEvents.MONITOR_EDIT_FAILURE;
      additionalContent: {
        MONITOR_ID?: string;
        ERROR_MESSAGE?: string;
      };
    }
  | {
      eventName: MixPanelEvents.HEALTH_SUMMARY_TIME_RANGE;
      additionalContent: {
        TIME_RANGE: string;
      };
    }
  | {
      eventName: MixPanelEvents.HEALTH_SUMMARY_ISSUE_CATEGORY;
      additionalContent: {
        ISSUE_CATEGORY: string;
      };
    }
  | {
      eventName: MixPanelEvents.HEALTH_SUMMARY_ISSUE_DATE;
      additionalContent: {
        ISSUE_DATE: string;
      };
    }
  | {
      eventName: MixPanelEvents.HEALTH_SUMMARY_DOWNLOAD;
      additionalContent?: undefined;
    }
  | {
      eventName:
        | MixPanelEvents.OUTREACH_CONFIG_SUPPORT_CREATE_SUCCESS
        | MixPanelEvents.OUTREACH_CONFIG_SUPPORT_UPDATE_SUCCESS;
      additionalContent: OutreachBaseContent;
    }
  | {
      eventName:
        | MixPanelEvents.OUTREACH_CONFIG_SUBSCRIBER_CREATE_SUCCESS
        | MixPanelEvents.OUTREACH_CONFIG_SUBSCRIBER_UPDATE_SUCCESS;
      additionalContent: OutreachBaseContent & {
        subject: string;
        message: string;
      };
    }
  | {
      eventName:
        | MixPanelEvents.OUTREACH_CONFIG_SUBSCRIBER_CREATE_FAILURE
        | MixPanelEvents.OUTREACH_CONFIG_SUBSCRIBER_UPDATE_FAILURE
        | MixPanelEvents.OUTREACH_CONFIG_SUPPORT_CREATE_FAILURE
        | MixPanelEvents.OUTREACH_CONFIG_SUPPORT_UPDATE_FAILURE
        | MixPanelEvents.OUTREACH_CONFIG_RETRIEVE_FAILURE;
      additionalContent: OutreachBaseContent & {
        ERROR_MESSAGE?: string;
      };
    }
  | {
      eventName:
        | MixPanelEvents.OUTREACH_CONFIG_SUPPORT_CREATE_FAILURE
        | MixPanelEvents.OUTREACH_CONFIG_SUPPORT_UPDATE_FAILURE;
      additionalContent: OutreachBaseContent & {
        ERROR_MESSAGE?: string;
      };
    }
  | {
      eventName:
        | MixPanelEvents.OUTREACH_CONFIG_SUBSCRIBER_CREATE_FAILURE
        | MixPanelEvents.OUTREACH_CONFIG_SUBSCRIBER_UPDATE_FAILURE;
      additionalContent: OutreachBaseContent & {
        ERROR_MESSAGE?: string;
        subject: string;
        message: string;
      };
    }
  | {
      eventName: MixPanelEvents.OUTREACH_CONFIG_RETRIEVE_FAILURE;
      additionalContent: Omit<OutreachBaseContent, 'ENABLED'> & {
        ERROR_MESSAGE?: string;
      };
    }
  | {
      eventName: MixPanelEvents.OUTREACH_TEST_NOTIFICATION_MODAL_OPEN;
      additionalContent: {
        OUTREACH_TYPE: OutreachTypes | 'undefined type';
      };
    }
  | {
      eventName:
        | MixPanelEvents.WEBHOOK_SUCCESS
        | MixPanelEvents.WEBHOOK_FAILURE;
      additionalContent: {
        INTERACTION: string;
        WEBHOOK_ID?: string;
        ERROR_MESSAGE?: string;
      };
    }
  | {
      eventName: MixPanelEvents.EMAIL_SUCCESS | MixPanelEvents.EMAIL_FAILURE;
      additionalContent: {
        INTERACTION: string;
        EMAIL_ID?: string;
        ERROR_MESSAGE?: string;
      };
    }
  | {
      eventName: MixPanelEvents.URL_SUCCESS | MixPanelEvents.URL_FAILURE;
      additionalContent: {
        INTERACTION: string;
        URL_ID?: string;
        ERROR_MESSAGE?: string;
      };
    }
  | {
      eventName: MixPanelEvents.INFO_MODAL_OPEN;
      additionalContent: {
        MODAL_TYPE: string;
        ERROR_MESSAGE?: string;
      };
    }
  | {
      eventName: MixPanelEvents.OUTREACH_EVENTS_MODAL_OPEN;
      additionalContent?: undefined;
    }
  | {
      eventName: MixPanelEvents.HEALTH_SUMMARY_ISSUE_MATRIX_CATEGORY_SELECT;
    }
  | {
      eventName: MixPanelEvents.FEEDBACK;
      additionalContent: {
        RATING?: string;
        MESSAGE?: string;
        SCREEN: string;
      };
    }
  | {
      eventName: MixPanelEvents.REORDER_MONITORS;
      additionalContent: {
        TOTAL_MONITORS: number;
        MONITORS_WITH_ORDER: number;
        PERCENTAGE_ORDERED: number;
      };
    }
  | {
      eventName: MixPanelEvents.HEALTH_SUMMARY_EXPAND_ISP_SCORES;
    }
  | {
      eventName: MixPanelEvents.HEALTH_SUMMARY_EDXPAND_ISSUE_MATRIX;
    };
